import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { goTo, RouterContext } from 't-lib-ui/router';
import { FlowFormLeaveContext, useFlowFormGlobal, ModalDialog, ButtonBar, FlowForm, HelpPopup, Popup, getKeyHandler } from 't-lib-ui/structure';
import { TextButton, Input, Checkbox, A, Spinner } from 't-lib-ui/widgets';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import MainLayout from '../components/MainLayout';
import PageHeader from '../components/PageHeader';
import HeaderAuthButtons from '../components/HeaderAuthButtons';
import FeedIcon from '../components/FeedIcon';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import PrivateFeedIcon from '../components/PrivateFeedIcon';
import MailIcon from '../components/MailIcon';
import FeedIssueIcon from '../components/FeedIssueIcon';
import FeedIssuePopup from '../components/FeedIssuePopup';
import { hasFeedIssue } from '../utils/feedIssue';
import ChevronRightIcon from '../components/ChevronRightIcon';
import MoreDotsIcon from '../components/MoreDotsIcon';
import DropdownIcon from '../components/DropdownIcon';
import SearchIcon from '../components/SearchIcon';
import QuestionMarkIcon from '../components/QuestionMarkIcon';
import PlusIcon from '../components/PlusIcon';
import FolderPlusIcon from '../components/FolderPlusIcon';
import { menuKeyDown, menuBlur } from '../components/menuUtils';
import RenameFeedPopup from '../components/RenameFeedPopup';
import { useLimitUnread } from '../components/LimitUnreadPopup';
import FeedInfoPopup from '../components/FeedInfoPopup';
import NewsletterInfoPopup from '../components/NewsletterInfoPopup';
import RssBankruptcyCard from '../components/RssBankruptcyCard';
import PageIntro from '../components/PageIntro';
import FeedLoadError from '../components/FeedLoadError';
import { useEngineState } from '../utils/useEngineState';
import ManageFeedsIcon from '../components/ManageFeedsIcon';
import { AppState } from '../context/general';
import { getMode } from '../connector/auth';
import { downloadOpml } from '../connector/api';
import { nonNull } from 't-lib-ui/helpers';
import { usePreferences } from '../connector/preferences';
import { formatUnreadCount } from '../utils/formatting';
import { RestUserFeed, RestFolderUpdate } from '../schema-copy/api-schema';
import { isSuperuser } from '../schema-copy/capabilities';
import { getPlanLimits } from '../schema-copy/userPlans';
import './MyFeedsPage.css';

const MAX_FOLDERS = 25;

// --- Small inline SVG icons (no emoji per project conventions) ---
const DragHandleIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" /></svg>
);
// Tri-state checkbox glyphs.
const TriCheck: React.FC = () => (
  <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5l3.5 3.5L13 4" /></svg>
);
const TriDash: React.FC = () => (
  <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3.5 8h9" /></svg>
);
// Neutral fallback when a feed has no favicon (an RSS glyph, not an emoji).
const GenericFeedIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1.5" fill="currentColor" stroke="none" /></svg>
);

interface FeedDropTarget {
  folder: string | null;
  beforeId: string | null; // insert before this feed id, or null = append at end of the group
}

const EMPTY_IDS: string[] = [];

// Search helpers (highlight, don't filter): all rows stay; matches get marked text + a row accent.
const matchesQuery = (text: string, query: string) => !!query && text.toLowerCase().includes(query);
const highlightText = (text: string, query: string): React.ReactNode => {
  if (!query) return text;
  const lc = text.toLowerCase();
  const out: React.ReactNode[] = [];
  let i = 0, idx: number;
  while ((idx = lc.indexOf(query, i)) >= 0) {
    if (idx > i) out.push(text.slice(i, idx));
    out.push(<mark className="mfMark" key={i}>{text.slice(idx, idx + query.length)}</mark>);
    i = idx + query.length;
  }
  if (i < text.length) out.push(text.slice(i));
  return out;
};

interface FeedRowProps {
  feed: RestUserFeed;
  nested: boolean;
  moving: boolean;
  selected: boolean;
  active: boolean;
  matchQuery: string; // the search query when this row's name matches it, else ''
  unread: number;
  showRead: 'u' | 'a';
  /** Options dropdown; non-null only for the row whose menu is open. */
  menu: React.ReactNode;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragEnd: () => void;
  onPaintOver: (id: string) => void;
  onPaintStart: (e: React.MouseEvent, id: string) => void;
  onCheckboxToggle: (id: string) => void;
  onContextMenu: (e: React.MouseEvent, id: string) => void;
  onToggleMode: (id: string) => void;
  onToggleMenu: (id: string) => void;
  onNewsletterInfo: (id: string) => void;
  onPrivateFeedInfo: () => void;
  onIssueInfo: (id: string) => void;
}

// One feed row. Memoized (all callback props must be reference-stable) so a search
// keystroke, unread tick, drag-over or selection change only re-renders the affected
// rows instead of all ~n feeds.
const FeedRow = memo(({ feed, nested, moving, selected, active, matchQuery, unread, showRead, menu, onDragStart, onDragEnd, onPaintOver, onPaintStart, onCheckboxToggle, onContextMenu, onToggleMode, onToggleMenu, onNewsletterInfo, onPrivateFeedInfo, onIssueInfo }: FeedRowProps) => {
  const id = feed.id;
  const name = feed.shortTitle || feed.title;
  const postCount = feed.postCount;
  const unreadEl = !postCount ? <span className="mfUnreadCount zero">0</span>
    : showRead == 'a' ? <span className={`mfDot ${unread ? 'unread' : ''}`} />
    : <span className={`mfUnreadCount ${!unread ? 'zero' : ''}`}>{formatUnreadCount(unread)}</span>;
  return (
    <div
      data-feedid={id}
      className={`mfRow mfFeedRow ${nested ? 'nested' : ''} ${moving ? 'mfDragging' : ''} ${matchQuery ? 'mfMatchRow' : ''} ${active ? 'mfMatchActive' : ''} ${menu ? 'mfMenuOpen' : ''}`}
      draggable
      onDragStart={e => onDragStart(e, id)}
      onDragEnd={onDragEnd}
      onMouseEnter={() => onPaintOver(id)}
      onContextMenu={e => onContextMenu(e, id)}
    >
      <div className="mfCheckboxCell" onMouseDown={e => onPaintStart(e, id)}>
        <Checkbox checked={selected} onToggle={() => onCheckboxToggle(id)} focussable="flow" size="small" />
      </div>
      <div className="mfContent">
        <span className="mfRowHandle" title="Drag to move"><DragHandleIcon size={16} /></span>
        <div className="mfIconCell">
          <FeedIcon className="feedIcon" src={getFeedIconUrl(feed, 48)} size={18} circular={feed.circularIcon} fallback={<GenericFeedIcon size={18} />} />
        </div>
        <div className="mfNameCell mfNameSelect" title={name} onMouseDown={e => onPaintStart(e, id)}>{highlightText(name, matchQuery)}</div>
        {feed.feedStatus == 'NEWSLETTER' && (
          <button id={`mfNlBadge-${id}`} className="lockBadge flowFocussable sidewayFocus" tabIndex={0} onClick={e => { e.stopPropagation(); onNewsletterInfo(id); }} onKeyDown={getKeyHandler(() => onNewsletterInfo(id))} title="Newsletter feed">
            <MailIcon size={12} color="currentColor" />
          </button>
        )}
        {feed.feedStatus == 'PRIVATE' && (
          <button className="lockBadge flowFocussable sidewayFocus" tabIndex={0} onClick={e => { e.stopPropagation(); onPrivateFeedInfo(); }} onKeyDown={getKeyHandler(onPrivateFeedInfo)} title="Private feed">
            <PrivateFeedIcon size={12} color="currentColor" />
          </button>
        )}
        {hasFeedIssue(feed) && (
          <button id={`mfIssueBadge-${id}`} className="lockBadge flowFocussable sidewayFocus" tabIndex={0} onClick={e => { e.stopPropagation(); onIssueInfo(id); }} onKeyDown={getKeyHandler(() => onIssueInfo(id))} title="This feed has a problem">
            <FeedIssueIcon size={12} color="var(--error-color)" />
          </button>
        )}
      </div>
      <div className="mfUnreadCell mfUnreadToggle flowFocussable sidewayFocus" tabIndex={0} role="button" title={showRead == 'a' ? 'No unread tracking' : 'Track unread count'} onClick={() => onToggleMode(id)} onKeyDown={getKeyHandler(() => onToggleMode(id))}>{unreadEl}</div>
      <div className="mfActionsCell">
        <div className="mfMenuContainer">
          <button className="mfIconButton flowFocussable sidewayFocus" tabIndex={0} title="Feed options" onClick={() => onToggleMenu(id)} onKeyDown={getKeyHandler(() => onToggleMenu(id))}><MoreDotsIcon size={20} /></button>
          {menu}
        </div>
      </div>
    </div>
  );
});

const MyFeedsPage: React.FC = () => {
  useFlowFormGlobal();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const engine = appState.feedEngine;
  const anon = getMode() == 'anon';
  const isAdmin = isSuperuser(appState.state.authData?.selectedMembership?.capabilities);
  const [preferences, updatePreferences] = usePreferences();
  const { openLimitUnread, limitUnreadPopup } = useLimitUnread();
  const canFilterPosts = !anon && getPlanLimits(appState.state.currentUser?.userPlan ?? 'free').keywordFilters;

  const [feeds, setFeeds] = useState<RestUserFeed[]>(() => engine.getFeeds());
  const [folders, setFolders] = useState<RestFolderUpdate>(() => engine.getFolders());
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  const [activeMatch, setActiveMatch] = useState(0); // index into the ordered match list

  // Open menus / dropdowns
  const [feedMenu, setFeedMenu] = useState<string | null>(null);
  const [folderMenu, setFolderMenu] = useState<string | null>(null);
  // Set when a menu is opened via right-click, so it appears at the pointer.
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [markReadMenu, setMarkReadMenu] = useState(false);
  const [barMenu, setBarMenu] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [privateFeedInfoOpen, setPrivateFeedInfoOpen] = useState(false);
  const openPrivateFeedInfo = useCallback(() => setPrivateFeedInfoOpen(true), []);
  const [newsletterInfoId, setNewsletterInfoId] = useState<string | null>(null);
  const [issueFeedId, setIssueFeedId] = useState<string | null>(null);
  const [feedDetailsId, setFeedDetailsId] = useState<string | null>(null);

  // Drag & drop
  const [dragType, setDragType] = useState<'feed' | 'folder' | null>(null);
  const [movingIds, setMovingIds] = useState<string[]>([]); // feeds being dragged (1, or a selected run)
  const [draggedFolder, setDraggedFolder] = useState<string | null>(null);
  const [feedDrop, setFeedDrop] = useState<FeedDropTarget | null>(null);
  const [folderDrop, setFolderDrop] = useState<string | null>(null);

  // Modals
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [renameFeedId, setRenameFeedId] = useState<string | null>(null);
  const [moveFeedId, setMoveFeedId] = useState<string | null>(null);
  const [moveSelectedOpen, setMoveSelectedOpen] = useState(false);
  const [unfollowIds, setUnfollowIds] = useState<string[] | null>(null);
  const [mergeSource, setMergeSource] = useState<string | null>(null);
  const [olderKeepOpen, setOlderKeepOpen] = useState(false);
  const [trackAllOpen, setTrackAllOpen] = useState(false);
  const [showTrackHelp, setShowTrackHelp] = useState(false);
  const [opmlExporting, setOpmlExporting] = useState(false);
  const [barWidth, setBarWidth] = useState(9999); // measured page width, drives responsive action bar
  const [confirmAll, setConfirmAll] = useState<{ message: string; onConfirm: () => void } | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  // Drag-to-paint selection: hold the mouse on a feed checkbox and move up/down to
  // invert every checkbox the pointer passes over.
  const paintingRef = useRef(false);
  const paintedRef = useRef<Set<string>>(new Set());
  const mouseToggleRef = useRef(false); // true while a mouse interaction owns the toggling

  // Subscribe to engine changes
  useEffect(() => {
    const seed = () => {
      const counts: Record<string, number> = {};
      for (const f of engine.getFeeds()) counts[f.id] = engine.getUnreadCount(f.id);
      setUnreadCounts(counts);
    };
    const unregFeeds = engine.onFeedsChange((newFeeds, newFolders) => {
      setFeeds([...newFeeds]);
      setFolders({ ...newFolders });
      seed();
    });
    const unregUnread = engine.onUnreadChange((feedId, count) =>
      setUnreadCounts(prev => ({ ...prev, [feedId]: count })));
    seed();
    return () => { unregFeeds(); unregUnread(); };
  }, [engine]);

  // End drag-to-paint selection on mouse release anywhere.
  useEffect(() => {
    const up = () => {
      if (!paintingRef.current) return;
      paintingRef.current = false;
      // Let the trailing click (if any) be ignored, then re-enable keyboard toggles.
      setTimeout(() => { mouseToggleRef.current = false; }, 0);
    };
    document.addEventListener('mouseup', up);
    return () => document.removeEventListener('mouseup', up);
  }, []);

  // Close any open dropdown/menu when clicking outside it.
  useEffect(() => {
    if (!feedMenu && !folderMenu && !markReadMenu && !barMenu) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('.mfMarkReadGroup')) setMarkReadMenu(false);
      if (!t.closest('.mfMenuContainer')) { setFeedMenu(null); setFolderMenu(null); setBarMenu(false); }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [feedMenu, folderMenu, markReadMenu, barMenu]);

  // Reset to the first match whenever the query changes.
  useEffect(() => { setActiveMatch(0); }, [search]);

  // Collapse the less-common action buttons into the "…" menu when the bar gets too
  // narrow to fit them on one line.
  useEffect(() => {
    const el = pageRef.current;
    if (!el || typeof ResizeObserver == 'undefined') return;
    const ro = new ResizeObserver(entries => setBarWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const canUseNewsletters = getPlanLimits(appState.state.currentUser?.userPlan ?? 'free').maxNewsletterFeeds > 0;

  const feedById = useMemo(() => new Map(feeds.map(f => [f.id, f])), [feeds]);
  const namedFolderNames = useMemo(() => Object.keys(folders).filter(k => k != ''), [folders]);

  // Changing values read through refs so the drag/paint callbacks below can stay
  // reference-stable for the memoized FeedRow.
  const selectedRef = useRef(selected); selectedRef.current = selected;
  const foldersRef = useRef(folders); foldersRef.current = folders;
  const feedByIdRef = useRef(feedById); feedByIdRef.current = feedById;
  const folderLimitReached = namedFolderNames.length >= MAX_FOLDERS;

  const getUnread = (id: string) => unreadCounts[id] ?? 0;
  const isAllRead = (id: string) => getUnread(id) == 0;
  const movingSet = new Set(movingIds);
  const engineState = useEngineState(engine);
  const feedsLoading = !feeds.length && (engineState == 'OFF' || engineState == 'LOADING');
  // Responsive action bar thresholds.
  const narrow = barWidth < 900;            // move less-common buttons into the "…" menu
  const selectedSecondLine = barWidth < 810; // show "N selected" on its own line
  const shortTrack = barWidth < 400;         // shorten "Track unread" to "Track"

  // Arrow up/down move between flow forms; left/right only at text boundaries (Enter/Escape handled inline).
  const searchKeyHandler = getKeyHandler({ enterBehaviour: 'ignore', spaceBehaviour: 'ignore', sidewaysBehaviour: 'bothText' });

  // When a context menu was opened by right-click, float it at the pointer (clamped to the viewport).
  const floatingMenuStyle: React.CSSProperties | undefined = menuPos
    ? { position: 'fixed', left: Math.min(menuPos.x, window.innerWidth - 210), top: Math.min(menuPos.y, window.innerHeight - 300), right: 'auto' }
    : undefined;
  // Only one menu open at a time.
  const closeAllMenus = () => { setFeedMenu(null); setFolderMenu(null); setMarkReadMenu(false); setBarMenu(false); };
  const toggleFeedMenu = useCallback((id: string) => {
    setMenuPos(null); setFolderMenu(null); setMarkReadMenu(false); setBarMenu(false);
    setFeedMenu(prev => prev == id ? null : id);
  }, []);
  const toggleFolderMenu = (name: string) => { setMenuPos(null); if (folderMenu == name) setFolderMenu(null); else { closeAllMenus(); setFolderMenu(name); } };
  const openFeedMenuAt = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    setFolderMenu(null); setMarkReadMenu(false); setBarMenu(false);
    setMenuPos({ x: e.clientX, y: e.clientY });
    setFeedMenu(id);
  }, []);
  const openFolderMenuAt = (e: React.MouseEvent, name: string) => { e.preventDefault(); closeAllMenus(); setMenuPos({ x: e.clientX, y: e.clientY }); setFolderMenu(name); };

  const query = search.trim().toLowerCase();

  const feedsInFolder = (name: string) =>
    (folders[name] ?? []).map(id => feedById.get(id)).filter((f): f is RestUserFeed => !!f);

  // --- Selection ---
  const toggleFeedSelected = useCallback((id: string) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  }), []);
  const setManySelected = (ids: string[], on: boolean) => setSelected(prev => {
    const next = new Set(prev);
    for (const id of ids) on ? next.add(id) : next.delete(id);
    return next;
  });
  const allFeedIds = feeds.map(f => f.id);
  const allSelected = !!allFeedIds.length && allFeedIds.every(id => selected.has(id));

  const startCheckboxPaint = useCallback((e: React.MouseEvent, id: string) => {
    if (e.button != 0) return;
    e.preventDefault(); // avoid text selection while dragging
    mouseToggleRef.current = true;
    paintingRef.current = true;
    paintedRef.current = new Set([id]);
    toggleFeedSelected(id);
  }, [toggleFeedSelected]);
  const paintCheckbox = useCallback((id: string) => {
    if (!paintingRef.current || paintedRef.current.has(id)) return;
    paintedRef.current.add(id);
    toggleFeedSelected(id);
  }, [toggleFeedSelected]);
  // Regular (keyboard/click) toggle; suppressed while a mouse paint interaction owns toggling.
  const checkboxToggle = useCallback((id: string) => {
    if (!mouseToggleRef.current) toggleFeedSelected(id);
  }, [toggleFeedSelected]);

  // --- Mark-as-read helpers ---
  const bulkMark = (label: string, apply: (ids: string[]) => void) => {
    if (selected.size) { apply(Array.from(selected)); return; }
    setConfirmAll({ message: `${label} for all ${feeds.length} feeds?`, onConfirm: () => apply(allFeedIds) });
  };

  const markReadAll = () => bulkMark('Mark all posts as read', ids => ids.forEach(id => engine.markAllRead(id)));
  const markOlder = (days: number) => bulkMark(`Mark posts older than ${days} day${days == 1 ? '' : 's'} as read`, ids => ids.forEach(id => engine.markOlderThanDaysRead(id, days)));
  const markOlderKeep = (days: number, keep: number) => {
    updatePreferences({ markReadOlderDays: days, markReadKeepCount: keep });
    bulkMark(`Mark posts older than ${days} day${days == 1 ? '' : 's'} as read, keeping the newest ${keep}`,
      ids => ids.forEach(id => engine.markOlderThanKeepRead(id, days, keep)));
  };

  // --- Unread tracking (tri-state checkbox) ---
  // Target = selected feeds, or all feeds when nothing is selected.
  const trackTargetIds = selected.size ? Array.from(selected) : allFeedIds;
  const trackModes = trackTargetIds.map(id => engine.getShowRead(id));
  const allTrack = trackModes.length > 0 && trackModes.every(m => m == 'u');
  const noneTrack = trackModes.length > 0 && trackModes.every(m => m == 'a');
  const trackState: 'checked' | 'unchecked' | 'indeterminate' =
    allTrack ? 'checked' : noneTrack ? 'unchecked' : 'indeterminate';

  const setFeedsTrack = (ids: string[], mode: 'u' | 'a') => {
    for (const id of ids) engine.setShowRead(id, mode);
    setFeeds([...engine.getFeeds()]);
  };
  const onTrackToggle = () => {
    if (!feeds.length) return;
    if (!selected.size) { setTrackAllOpen(true); return; }
    // checked -> turn all off; unchecked or indeterminate -> turn all on.
    setFeedsTrack(Array.from(selected), trackState == 'checked' ? 'a' : 'u');
  };

  // --- OPML import / export (same as AccountPage) ---
  const handleOpmlExport = async () => {
    if (opmlExporting) return;
    setOpmlExporting(true);
    try {
      await downloadOpml(nonNull(appState.state.authData));
    } catch (e) {
      console.error('OPML export failed:', e);
    } finally {
      setOpmlExporting(false);
    }
  };

  // --- Per-feed actions ---
  const toggleFeedMode = useCallback((id: string) => {
    engine.setShowRead(id, engine.getShowRead(id) == 'u' ? 'a' : 'u');
    setFeeds([...engine.getFeeds()]);
  }, [engine]);
  const toggleFeedRead = (id: string) => {
    isAllRead(id) ? engine.markAllUnread(id) : engine.markAllRead(id);
  };
  const moveFeedTo = async (id: string, folder: string | null) => { await engine.moveFeed(id, folder); };
  const moveSelectedTo = async (folder: string | null) => {
    for (const id of Array.from(selected)) await engine.moveFeed(id, folder);
    setSelected(new Set());
    setMoveSelectedOpen(false);
  };
  const unfollow = async (ids: string[]) => {
    for (const id of ids) await engine.unsubscribe(id);
    setManySelected(ids, false);
  };

  // --- Folder actions ---
  const toggleExpanded = (name: string) => setExpanded(p => ({ ...p, [name]: !(p[name] ?? true) }));

  const createFolder = async (name: string) => {
    const ok = await engine.addEmptyFolder(name);
    if (ok && selected.size) {
      for (const id of Array.from(selected)) await engine.moveFeed(id, name);
      setSelected(new Set());
    }
  };
  const removeFolder = async (name: string) => { await engine.deleteFolder(name, false); };
  const mergeInto = async (target: string) => {
    if (mergeSource) await engine.mergeFolders(mergeSource, target);
    setMergeSource(null);
  };
  const commitFolderRename = async () => {
    if (renamingFolder && renameFolderValue.trim() && renameFolderValue.trim() != renamingFolder)
      await engine.renameFolder(renamingFolder, renameFolderValue.trim());
    setRenamingFolder(null);
  };

  // --- Feed drag & drop ---
  const onFeedDragStart = useCallback((e: React.DragEvent, id: string) => {
    const folders = foldersRef.current, selected = selectedRef.current;
    const groupOf = (feedId: string): string => {
      for (const [name, ids] of Object.entries(folders)) if (ids.includes(feedId)) return name;
      return '';
    };
    // When dragging a feed that is part of a contiguous run of selected feeds within the
    // same group, move the whole run; otherwise just the one feed.
    const computeMovingIds = (): string[] => {
      if (!selected.has(id) || selected.size < 2) return [id];
      const list = folders[groupOf(id)] ?? [];
      const idx = list.indexOf(id);
      if (idx < 0) return [id];
      let a = idx, b = idx;
      while (a - 1 >= 0 && selected.has(list[a - 1])) a--;
      while (b + 1 < list.length && selected.has(list[b + 1])) b++;
      const run = list.slice(a, b + 1);
      return run.length > 1 ? run : [id];
    };
    // Off-screen drag image showing the icons of all moving feeds (up to 8).
    const buildMultiDragImage = (ids: string[]): HTMLElement => {
      const el = document.createElement('div');
      el.className = 'mfDragImage';
      for (const fid of ids.slice(0, 8)) {
        const feed = feedByIdRef.current.get(fid);
        if (feed) {
          const img = document.createElement('img');
          img.src = getFeedIconUrl(feed, 48);
          if (feed.circularIcon) img.className = 'feedIconCircular';
          el.appendChild(img);
        } else {
          const span = document.createElement('span');
          span.className = 'mfDragImageGeneric';
          el.appendChild(span);
        }
      }
      document.body.appendChild(el);
      return el;
    };

    setDragType('feed');
    const ids = computeMovingIds();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    if (ids.length > 1) {
      const img = buildMultiDragImage(ids);
      e.dataTransfer.setDragImage(img, 24, 18);
      setTimeout(() => img.remove(), 0);
    }
    // Hide the source row(s) only after the drag has actually started — hiding them
    // synchronously inside dragstart cancels the drag in most browsers.
    setTimeout(() => setMovingIds(ids), 0);
  }, []);
  const onDragEnd = useCallback(() => { setDragType(null); setMovingIds([]); setDraggedFolder(null); setFeedDrop(null); setFolderDrop(null); }, []);

  // dragover fires continuously while dragging; returning the previous object when the
  // target hasn't moved lets React bail out instead of re-rendering the whole page.
  const updateFeedDrop = (folder: string | null, beforeId: string | null) =>
    setFeedDrop(prev => prev && prev.folder == folder && prev.beforeId == beforeId ? prev : { folder, beforeId });

  // Insertion index among the *visible* (non-moving) rows of the container. The
  // placeholder currently in the list pushes the rows below it down, so we normalize
  // those rows back to their no-placeholder positions before comparing — otherwise the
  // index lags by one when dragging downward.
  const onFeedContainerDragOver = (e: React.DragEvent, folder: string | null) => {
    if (dragType != 'feed') return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    const container = e.currentTarget as HTMLElement;
    const rows = Array.from(container.querySelectorAll<HTMLElement>(':scope > .mfFeedRow:not(.mfDragging)'));
    const ph = container.querySelector<HTMLElement>(':scope > .mfDropPlaceholder');
    let phH = 0;
    if (ph) {
      const s = getComputedStyle(ph);
      phH = ph.getBoundingClientRect().height + parseFloat(s.marginTop) + parseFloat(s.marginBottom);
    }
    // Index of the row the placeholder currently sits before, so we can normalize away
    // the gap the placeholder itself creates (otherwise the index lags when going down).
    const curBefore = feedDrop && feedDrop.folder == folder ? feedDrop.beforeId : undefined;
    const curPos = curBefore === undefined ? -1
      : curBefore === null ? rows.length
      : rows.findIndex(r => r.dataset.feedid == curBefore);
    const y = e.clientY;
    let beforeId: string | null = null;
    for (let i = 0; i < rows.length; i++) {
      const rect = rows[i].getBoundingClientRect();
      const top = rect.top - (curPos >= 0 && i >= curPos ? phH : 0);
      // Use the row's bottom edge (half a line below the midpoint) so hovering anywhere
      // over a row inserts *before* it — feels more natural than a midpoint threshold.
      if (y < top + rect.height) { beforeId = rows[i].dataset.feedid ?? null; break; }
    }
    updateFeedDrop(folder, beforeId);
  };

  const onFeedDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (dragType != 'feed' || !feedDrop || !movingIds.length) { onDragEnd(); return; }
    const ids = movingIds;
    const folder = feedDrop.folder;
    const beforeId = feedDrop.beforeId;
    onDragEnd();
    // Compute the insertion index from the same data model moveFeeds operates on: the
    // target folder's list with the moving feeds removed, then the index of the anchor.
    const moving = new Set(ids);
    const postList = (folders[folder ?? ''] ?? []).filter(id => !moving.has(id));
    const at = beforeId ? postList.indexOf(beforeId) : -1;
    await engine.moveFeeds(ids, folder, at >= 0 ? at : postList.length);
  };

  // --- Folder drag & drop (reorder whole folders) ---
  const onFolderDragStart = (e: React.DragEvent, name: string) => {
    setDragType('folder');
    setDraggedFolder(name);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', name);
  };
  const onFolderDragOver = (e: React.DragEvent, name: string) => {
    // Dropping a feed onto the folder header drops it into that folder (appended at the end).
    if (dragType == 'feed') {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      updateFeedDrop(name, null);
      return;
    }
    if (dragType != 'folder') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setFolderDrop(name);
  };
  const onFolderDrop = async (e: React.DragEvent) => {
    if (dragType == 'feed') { await onFeedDrop(e); return; }
    e.preventDefault();
    if (dragType != 'folder' || !draggedFolder || !folderDrop || draggedFolder == folderDrop) { onDragEnd(); return; }
    const order = [...namedFolderNames];
    const from = order.indexOf(draggedFolder);
    order.splice(from, 1);
    const to = order.indexOf(folderDrop);
    order.splice(to, 0, draggedFolder);
    await engine.setFolderOrder(order);
    onDragEnd();
  };

  // --- Renderers ---
  const renderFeedMenu = (id: string) => {
    if (feedMenu != id) return null;
    const close = () => setFeedMenu(null);
    return (
      <div className="mfMenu flowFormModal visibleFocus" style={floatingMenuStyle} onKeyDown={menuKeyDown(close)} onBlur={menuBlur(close)} onClick={e => e.stopPropagation()}>
        <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { toggleFeedRead(id); close(); }}>
          {isAllRead(id) ? 'Mark unread' : 'Mark as read'}
        </button>
        <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { setRenameFeedId(id); close(); }}>Rename feed</button>
        <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { setMoveFeedId(id); close(); }}>Move to folder…</button>
        <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { openLimitUnread(id); close(); }}>Limit unread posts…</button>
        {canFilterPosts && <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { goTo(routerCtx, `/a/filter/${id}`); close(); }}>Filter posts…</button>}
        <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { setFeedDetailsId(id); close(); }}>Feed details</button>
        {isAdmin && (
          <>
            <div className="mfMenuDivider" />
            <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { goTo(routerCtx, `/a/admin/feed/${id}`); close(); }}>Admin Feed</button>
            <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { goTo(routerCtx, `/a/admin/catalog/${id}`); close(); }}>Admin Catalog</button>
          </>
        )}
        <div className="mfMenuDivider" />
        <button className="mfMenuItem danger flowFocussable" tabIndex={0} onClick={() => { setUnfollowIds([id]); close(); }}>Unfollow</button>
      </div>
    );
  };

  // Render a group's feed rows, inserting a single drop-placeholder (a row-sized gap)
  // at the current insertion point so users see where the dragged item(s) will land.
  const renderFeedGroup = (groupFeeds: RestUserFeed[], folder: string | null) => {
    const dropHere = dragType == 'feed' && !!feedDrop && feedDrop.folder == folder;
    const beforeId = dropHere ? feedDrop!.beforeId : undefined;
    const out: React.ReactNode[] = [];
    for (const f of groupFeeds) {
      const moving = movingSet.has(f.id);
      if (!moving && beforeId === f.id) out.push(<div key="mf-ph" className="mfDropPlaceholder" />);
      out.push(<FeedRow
        key={f.id}
        feed={f}
        nested={folder != null}
        moving={moving}
        selected={selected.has(f.id)}
        active={activeKey == 'feed:' + f.id}
        matchQuery={matchesQuery(f.shortTitle || f.title, query) ? query : ''}
        unread={getUnread(f.id)}
        showRead={engine.getShowRead(f.id)}
        menu={renderFeedMenu(f.id)}
        onDragStart={onFeedDragStart}
        onDragEnd={onDragEnd}
        onPaintOver={paintCheckbox}
        onPaintStart={startCheckboxPaint}
        onCheckboxToggle={checkboxToggle}
        onContextMenu={openFeedMenuAt}
        onToggleMode={toggleFeedMode}
        onToggleMenu={toggleFeedMenu}
        onNewsletterInfo={setNewsletterInfoId}
        onPrivateFeedInfo={openPrivateFeedInfo}
        onIssueInfo={setIssueFeedId}
      />);
    }
    if (dropHere && beforeId === null) out.push(<div key="mf-ph-end" className="mfDropPlaceholder" />);
    return out;
  };

  const renderFolderMenu = (name: string) => {
    if (folderMenu != name) return null;
    const close = () => setFolderMenu(null);
    return (
      <div className="mfMenu flowFormModal visibleFocus" style={floatingMenuStyle} onKeyDown={menuKeyDown(close)} onBlur={menuBlur(close)} onClick={e => e.stopPropagation()}>
        <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { setRenamingFolder(name); setRenameFolderValue(name); close(); }}>Rename folder</button>
        <button className="mfMenuItem flowFocussable" tabIndex={0} disabled={namedFolderNames.length < 2} onClick={() => { setMergeSource(name); close(); }}>Merge into…</button>
        <div className="mfMenuDivider" />
        <button className="mfMenuItem danger flowFocussable" tabIndex={0} onClick={() => { removeFolder(name); close(); }}>Remove folder</button>
      </div>
    );
  };

  const renderFolder = (name: string) => {
    const folderFeeds = feedsInFolder(name);
    const ids = folderFeeds.map(f => f.id);
    // While searching, force folders open so matches inside them are visible.
    const isExpanded = query ? true : (expanded[name] ?? true);
    // Folder count reflects only tracked ('u') feeds — untracked ('a') feeds show a dot, not a
    // number. When both kinds have unread posts, append '+' to the tracked count so the badge
    // signals "and some untracked posts too" (e.g. '8+'); a folder with only untracked unread
    // shows a bare dot.
    const folderTrackedUnread = folderFeeds.reduce((s, f) => s + (engine.getShowRead(f.id) == 'u' ? getUnread(f.id) : 0), 0);
    const folderHasUntrackedUnread = folderFeeds.some(f => engine.getShowRead(f.id) == 'a' && getUnread(f.id) > 0);
    const folderTrackedStr = formatUnreadCount(folderTrackedUnread);
    const folderCountLabel = folderHasUntrackedUnread && !folderTrackedStr.endsWith('+') ? folderTrackedStr + '+' : folderTrackedStr;
    const allFolderSelected = !!ids.length && ids.every(id => selected.has(id));
    return (
      <div key={name}>
        <div
          className={`mfFolderRow ${(dragType == 'folder' && folderDrop == name) || (dragType == 'feed' && feedDrop?.folder == name) ? 'dragOver' : ''} ${matchesQuery(name, query) ? 'mfMatchRow' : ''} ${activeKey == 'folder:' + name ? 'mfMatchActive' : ''}`}
          onClick={() => renamingFolder != name && toggleExpanded(name)}
          onContextMenu={e => openFolderMenuAt(e, name)}
          onDragOver={e => onFolderDragOver(e, name)}
          onDrop={onFolderDrop}
        >
          <div className="mfCheckboxCell" onClick={e => e.stopPropagation()}>
            <Checkbox checked={allFolderSelected} onToggle={() => setManySelected(ids, !allFolderSelected)} focussable="flow" size="small" />
          </div>
          <div className="mfFolderMain">
            <span className="mfRowHandle" draggable onDragStart={e => onFolderDragStart(e, name)} onDragEnd={onDragEnd} title="Drag to reorder"><DragHandleIcon size={16} /></span>
            <span className={`mfFolderExpand flowFocussable sidewayFocus ${isExpanded ? 'expanded' : ''}`} tabIndex={0} role="button" title={isExpanded ? 'Collapse' : 'Expand'} onClick={e => { e.stopPropagation(); toggleExpanded(name); }} onKeyDown={getKeyHandler(() => toggleExpanded(name))}><ChevronRightIcon size={18} /></span>
            {renamingFolder == name ? (
              <input
                className="mfFolderNameInput"
                autoFocus
                value={renameFolderValue}
                maxLength={32}
                onClick={e => e.stopPropagation()}
                onChange={e => setRenameFolderValue(e.target.value.replace(/(^\s|[\n\r\t])/g, '').slice(0, 32))}
                onBlur={commitFolderRename}
                onKeyDown={e => { if (e.key == 'Enter') commitFolderRename(); else if (e.key == 'Escape') setRenamingFolder(null); }}
              />
            ) : (
              <span className="mfFolderName">{highlightText(name, query)}</span>
            )}
          </div>
          <div className="mfUnreadCell">
            {!isExpanded && (folderTrackedUnread > 0
              ? <span className="mfUnreadCount">{folderCountLabel}</span>
              : folderHasUntrackedUnread ? <span className="mfDot unread" /> : null)}
          </div>
          <div className="mfActionsCell" onClick={e => e.stopPropagation()}>
            <div className="mfMenuContainer">
              <button className="mfIconButton flowFocussable sidewayFocus" tabIndex={0} title="Folder options" onClick={() => toggleFolderMenu(name)} onKeyDown={getKeyHandler(() => toggleFolderMenu(name))}><MoreDotsIcon size={20} /></button>
              {renderFolderMenu(name)}
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="mfFolderContents" onDragOver={e => onFeedContainerDragOver(e, name)} onDrop={onFeedDrop}>
            {!folderFeeds.length && <div className="mfFolderEmpty">Drag feeds here to organize them</div>}
            {renderFeedGroup(folderFeeds, name)}
          </div>
        )}
      </div>
    );
  };

  // Feeds in the explicit unfiled order, plus any feed not in any folder yet (e.g. a freshly
  // created newsletter that the server hasn't placed into the unfiled bucket on a reload) —
  // mirrors LeftSidebar so newly-added feeds always appear.
  const unfiledFeeds = useMemo(() => {
    const unfiledIds = folders[''] ?? EMPTY_IDS;
    const feedsInNamedFolders = new Set(namedFolderNames.flatMap(n => folders[n] ?? []));
    const unfiledIdSet = new Set(unfiledIds);
    const orderedUnfiled = unfiledIds.map(id => feedById.get(id)).filter((f): f is RestUserFeed => !!f);
    const remainingUnfiled = feeds.filter(f => !feedsInNamedFolders.has(f.id) && !unfiledIdSet.has(f.id));
    return [...orderedUnfiled, ...remainingUnfiled];
  }, [feeds, folders, feedById, namedFolderNames]);

  // Ordered list of search matches (DOM order) and the current "primary" match.
  const matchList = useMemo(() => {
    if (!query) return EMPTY_IDS;
    const out: string[] = [];
    for (const folderName of namedFolderNames) {
      if (matchesQuery(folderName, query)) out.push('folder:' + folderName);
      for (const id of folders[folderName] ?? [])
        if (matchesQuery((feedById.get(id)?.shortTitle || feedById.get(id)?.title) ?? '', query)) out.push('feed:' + id);
    }
    for (const f of unfiledFeeds)
      if (matchesQuery(f.shortTitle || f.title, query)) out.push('feed:' + f.id);
    return out;
  }, [query, folders, feedById, namedFolderNames, unfiledFeeds]);
  const activeKey = matchList.length
    ? matchList[((activeMatch % matchList.length) + matchList.length) % matchList.length]
    : null;

  // Scroll the primary match into view when it changes (no-op if already visible).
  useEffect(() => {
    if (!activeKey) return;
    scrollRef.current?.querySelector<HTMLElement>('.mfMatchActive')?.scrollIntoView({ block: 'nearest' });
  }, [activeKey]);

  const cycleMatch = (delta: number) => {
    if (matchList.length) setActiveMatch(a => a + delta);
  };

  const right = ({ onExpand }: { onExpand: () => void }) => (
    <div className="myFeedsPage" ref={pageRef}>
      <PageHeader title="My feeds" onMenu={onExpand} rightContent={<HeaderAuthButtons />} />

      <div className="mfIntro">
        <PageIntro
          title="Organize your feeds"
          icon={<ManageFeedsIcon size={24} />}
          subtitle="Organize feeds into folders, rename them, and choose how unread counts are tracked."
        />
      </div>

      <FlowForm main className="mfActionBar">
        <Checkbox checked={allSelected} onToggle={() => setManySelected(allFeedIds, !allSelected)} focussable="flow" size="small" />
        {selected.size > 0 && !selectedSecondLine && <span className="mfSelectionInfo">{selected.size} selected</span>}

        {!narrow && <button className={`mfTextButton ${!folderLimitReached ? 'flowFocussable sidewayFocus' : ''}`} tabIndex={0} disabled={folderLimitReached} onClick={() => setCreateFolderOpen(true)} onKeyDown={getKeyHandler(() => setCreateFolderOpen(true))}><FolderPlusIcon size={14} /> New folder</button>}
        {!narrow && <button className="mfTextButton flowFocussable sidewayFocus" tabIndex={0} onClick={() => goTo(routerCtx, '/a/add')} onKeyDown={getKeyHandler(() => goTo(routerCtx, '/a/add'))}><PlusIcon size={14} /> Add feed</button>}
        {!narrow && <button className={`mfTextButton ${selected.size ? 'flowFocussable sidewayFocus' : ''}`} tabIndex={0} disabled={!selected.size} onClick={() => setMoveSelectedOpen(true)} onKeyDown={getKeyHandler(() => setMoveSelectedOpen(true))}>Move to…</button>}
        {!narrow && <button className={`mfTextButton ${selected.size ? 'flowFocussable sidewayFocus' : ''}`} tabIndex={0} disabled={!selected.size} onClick={() => setUnfollowIds(Array.from(selected))} onKeyDown={getKeyHandler(() => setUnfollowIds(Array.from(selected)))}>Unfollow</button>}

        <div className="mfMarkReadGroup">
          <button className="mfTextButton flowFocussable sidewayFocus" tabIndex={0} onClick={markReadAll} onKeyDown={getKeyHandler(markReadAll)}>Mark as read</button>
          <button className="mfTextButton mfDropdownToggle flowFocussable sidewayFocus" tabIndex={0} title="Mark read options" onClick={() => { if (markReadMenu) setMarkReadMenu(false); else { closeAllMenus(); setMarkReadMenu(true); } }} onKeyDown={getKeyHandler(() => { closeAllMenus(); setMarkReadMenu(true); })}><DropdownIcon size={14} /></button>
          {markReadMenu && (
            <div className="mfMenu flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setMarkReadMenu(false))} onBlur={menuBlur(() => setMarkReadMenu(false))}>
              <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { markOlder(1); setMarkReadMenu(false); }}>Older than one day</button>
              <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { markOlder(7); setMarkReadMenu(false); }}>Older than one week</button>
              <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { markOlder(30); setMarkReadMenu(false); }}>Older than one month</button>
              <div className="mfMenuDivider" />
              <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { setOlderKeepOpen(true); setMarkReadMenu(false); }}>Older than X days, keep Y…</button>
            </div>
          )}
        </div>

        <button className={`mfTriToggle ${feeds.length ? 'flowFocussable sidewayFocus' : ''}`} tabIndex={0} disabled={!feeds.length} title="Track unread counts for the selected feeds (or all feeds)" onClick={onTrackToggle} onKeyDown={getKeyHandler(onTrackToggle)}>
          <span className={`mfTriBox ${trackState}`}>
            {trackState == 'checked' && <TriCheck />}
            {trackState == 'indeterminate' && <TriDash />}
          </span>
          {shortTrack ? 'Track' : 'Track unread'}
          <span className="mfTriHelp flowFocussable sidewayFocus" tabIndex={0} role="button" title="What does this mean?" onClick={e => { e.stopPropagation(); setShowTrackHelp(true); }} onKeyDown={e => { e.stopPropagation(); getKeyHandler(() => setShowTrackHelp(true))(e); }}><QuestionMarkIcon size={14} /></span>
        </button>

        <span className="mfActionSpacer" />
        <div className="mfMenuContainer">
          <button className="mfIconButton mfActionMenuButton flowFocussable sidewayFocus" tabIndex={0} title="More options" onClick={() => { setMenuPos(null); if (barMenu) setBarMenu(false); else { closeAllMenus(); setBarMenu(true); } }} onKeyDown={getKeyHandler(() => { setMenuPos(null); closeAllMenus(); setBarMenu(true); })}><MoreDotsIcon size={18} /></button>
          {barMenu && (
            <div className="mfMenu flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setBarMenu(false))} onBlur={menuBlur(() => setBarMenu(false))}>
              {narrow && (
                <>
                  <button className="mfMenuItem flowFocussable" tabIndex={0} disabled={folderLimitReached} onClick={() => { setCreateFolderOpen(true); setBarMenu(false); }}>New folder</button>
                  <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { goTo(routerCtx, '/a/add'); setBarMenu(false); }}>Add feed</button>
                  <button className="mfMenuItem flowFocussable" tabIndex={0} disabled={!selected.size} onClick={() => { setMoveSelectedOpen(true); setBarMenu(false); }}>Move to…</button>
                  <button className="mfMenuItem flowFocussable" tabIndex={0} disabled={!selected.size} onClick={() => { setUnfollowIds(Array.from(selected)); setBarMenu(false); }}>Unfollow</button>
                  <div className="mfMenuDivider" />
                </>
              )}
              {canUseNewsletters && (
                <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { goTo(routerCtx, '/a/add/newsletter'); setBarMenu(false); }}>Add newsletter inbox…</button>
              )}
              <button className="mfMenuItem flowFocussable" tabIndex={0} onClick={() => { goTo(routerCtx, '/a/import'); setBarMenu(false); }}>Import OPML…</button>
              <button className="mfMenuItem flowFocussable" tabIndex={0} disabled={opmlExporting} onClick={() => { handleOpmlExport(); setBarMenu(false); }}>Export OPML</button>
            </div>
          )}
        </div>
      </FlowForm>

      {selectedSecondLine && (
        <div className="mfSelectedLine">{selected.size > 0 ? `${selected.size} selected` : ' '}</div>
      )}

      <FlowForm className="mfMainForm">
      <div className="mfSearchBar">
        <span className="mfSearchIcon"><SearchIcon size={16} /></span>
        <input
          className="mfSearchInput flowFocussable"
          type="text"
          value={search}
          placeholder="Search my feeds"
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            if (e.key == 'Escape') { setSearch(''); return; }
            if (e.key == 'Enter') { e.preventDefault(); cycleMatch(e.shiftKey ? -1 : 1); return; }
            searchKeyHandler(e);
          }}
        />
        {query && <span className="mfSearchCount">{matchList.length ? (((activeMatch % matchList.length) + matchList.length) % matchList.length) + 1 : 0}/{matchList.length}</span>}
        {search && <button className="mfSearchClear flowFocussable sidewayFocus" tabIndex={0} title="Clear search" onClick={() => setSearch('')} onKeyDown={getKeyHandler(() => setSearch(''))}>×</button>}
      </div>

      <div className="mfListScroll" ref={scrollRef}>
        <RssBankruptcyCard context="myfeeds" />
        {feedsLoading ? (
          <div className="mfEmptyState"><Spinner spinning /></div>
        ) : engineState == 'ERROR' ? (
          // Never show the "no feeds yet" empty state after a failed load — it
          // reads as "your subscriptions are gone".
          <FeedLoadError />
        ) : !feeds.length ? (
          <div className="mfEmptyState">You don't follow any feeds yet. Use "Add feed" to get started.</div>
        ) : (
          <div className="mfList">
            {namedFolderNames.map(renderFolder)}
            <div className="mfFolderContents mfUnfiledContents" onDragOver={e => onFeedContainerDragOver(e, null)} onDrop={onFeedDrop}>
              {renderFeedGroup(unfiledFeeds, null)}
            </div>
          </div>
        )}
        {feeds.length > 0 && feeds.length < 5 && (
          <div className="mfMoreFeedsHint">
            {anon
              ? <>Want more to read? Explore recommendations on <A href="/a/discover" internal>Discover</A>, or search our <A href="/a/add" internal>catalog</A>.</>
              : <>Want more to read? Explore recommendations on <A href="/a/discover" internal>Discover</A>, <A href="/a/add" internal>add a feed</A> by its URL, or <A href="/a/import" internal>import an OPML file</A>.</>
            }
          </div>
        )}
      </div>
      </FlowForm>

      {/* Modals */}
      {renameFeedId && (() => {
        const feed = feedById.get(renameFeedId);
        if (!feed) return null;
        const currentCustomName = preferences.customFeedNames?.[renameFeedId];
        return (
          <RenameFeedPopup
            show
            originalName={feed.title}
            currentCustomName={currentCustomName}
            onClose={() => setRenameFeedId(null)}
            onSave={name => { updatePreferences({ customFeedNames: { ...(preferences.customFeedNames ?? {}), [renameFeedId]: name } }); setRenameFeedId(null); }}
            onReset={() => { const next = { ...(preferences.customFeedNames ?? {}) }; delete next[renameFeedId]; updatePreferences({ customFeedNames: next }); setRenameFeedId(null); }}
          />
        );
      })()}

      <CreateFolderModal
        show={createFolderOpen}
        existing={namedFolderNames}
        autoAddCount={selected.size}
        onClose={() => setCreateFolderOpen(false)}
        onCreate={async name => { await createFolder(name); setCreateFolderOpen(false); }}
      />

      {moveFeedId && (
        <MoveToFolderModal
          show
          folders={namedFolderNames}
          currentFolder={namedFolderNames.find(n => (folders[n] ?? []).includes(moveFeedId)) ?? null}
          canCreate={!folderLimitReached}
          onClose={() => setMoveFeedId(null)}
          onPick={async folder => { await moveFeedTo(moveFeedId, folder); setMoveFeedId(null); }}
          onCreateAndMove={async name => { await engine.addEmptyFolder(name); await moveFeedTo(moveFeedId, name); setMoveFeedId(null); }}
        />
      )}

      {moveSelectedOpen && (
        <MoveToFolderModal
          show
          folders={namedFolderNames}
          currentFolder=""
          canCreate={!folderLimitReached}
          onClose={() => setMoveSelectedOpen(false)}
          onPick={folder => moveSelectedTo(folder)}
          onCreateAndMove={async name => { await engine.addEmptyFolder(name); await moveSelectedTo(name); }}
        />
      )}

      {unfollowIds && (
        <ConfirmListModal
          show
          title="Unfollow feeds"
          confirmLabel="Unfollow"
          message={`Unfollow ${unfollowIds.length} feed${unfollowIds.length == 1 ? '' : 's'}?`}
          items={unfollowIds.map(id => feedById.get(id)?.shortTitle || feedById.get(id)?.title || id)}
          onClose={() => setUnfollowIds(null)}
          onConfirm={async () => { const ids = unfollowIds; setUnfollowIds(null); await unfollow(ids); }}
        />
      )}

      {mergeSource && (
        <MergeFolderModal
          show
          source={mergeSource}
          targets={namedFolderNames.filter(n => n != mergeSource)}
          onClose={() => setMergeSource(null)}
          onMerge={mergeInto}
        />
      )}

      {olderKeepOpen && (
        <OlderKeepModal
          show
          defaultDays={preferences.markReadOlderDays ?? 14}
          defaultKeep={preferences.markReadKeepCount ?? 0}
          onClose={() => setOlderKeepOpen(false)}
          onSubmit={(days, keep) => { markOlderKeep(days, keep); setOlderKeepOpen(false); }}
        />
      )}

      <HelpPopup show={showTrackHelp} title="How do you want to read it?" onClose={() => setShowTrackHelp(false)} closable={true}>
        <div>
          <strong>Track unread counts</strong> keeps count of how many posts you haven't read yet.
          The unread count is shown next to the feed, so you can read every post and see exactly how
          many are left.
        </div>
        <img
          src="/unread-count.png"
          alt="A feed showing its number of unread posts"
          style={{ display: 'block', width: '100%', maxWidth: '420px', margin: '12px auto', borderRadius: '6px' }}
        />
        <div style={{ marginTop: '12px' }}>
          <strong>No unread tracking</strong> doesn't keep a count. Instead of a number, the feed only
          shows a dot to let you know new posts have arrived. Handy for feeds you just browse
          occasionally without needing to read everything.
        </div>
      </HelpPopup>

      {trackAllOpen && (
        <ModalDialog show title="Track unread for all feeds" closeButton="close" onClose={() => setTrackAllOpen(false)} className="mfModal">
          <p>Track unread counts for all {feeds.length} feeds?</p>
          <ButtonBar className="full-margin-top" align="right">
            <TextButton type="minor" onClick={() => setTrackAllOpen(false)} label="Cancel" />
            <TextButton type="minor" focussable="sideway" onClick={() => { setFeedsTrack(allFeedIds, 'a'); setTrackAllOpen(false); }} label="Track none" />
            <TextButton type="major" focussable="sideway" onClick={() => { setFeedsTrack(allFeedIds, 'u'); setTrackAllOpen(false); }} label="Track all" />
          </ButtonBar>
        </ModalDialog>
      )}

      {(() => {
        const userPlan = appState.state.currentUser?.userPlan;
        const privateCount = feeds.filter(f => f.feedStatus == 'PRIVATE').length;
        const maxPrivate = getPlanLimits(userPlan ?? 'free').maxPrivateFeeds;
        const remaining = maxPrivate - privateCount;
        return (
          <Popup show={privateFeedInfoOpen} title="Private Feed" onClose={() => setPrivateFeedInfoOpen(false)} closable minWidth="300px">
            <div className="privateFeedInfoPopup">
              <p>This is a <strong>private feed</strong>. Others cannot see or access it unless they add the exact same URL. It will not appear in any public listings.</p>
              <p>You are using <strong>{privateCount} of {maxPrivate}</strong> private feed{maxPrivate != 1 ? 's' : ''} on your plan{remaining > 0 ? `, with ${remaining} remaining` : ' (limit reached)'}.</p>
              <p>Private feeds are intended for paid subscriptions and personalized feeds, such as content behind a paywall or feeds containing matches for a search term.</p>
            </div>
          </Popup>
        );
      })()}

      <NewsletterInfoPopup
        show={!!newsletterInfoId}
        email={(newsletterInfoId ? feedById.get(newsletterInfoId)?.url ?? '' : '').replace(/^mailto:/i, '')}
        feedTitle={newsletterInfoId ? feedById.get(newsletterInfoId)?.title : undefined}
        closeFocus={newsletterInfoId ? `mfNlBadge-${newsletterInfoId}` : undefined}
        onClose={() => setNewsletterInfoId(null)}
      />

      <FeedIssuePopup
        show={!!issueFeedId}
        fetchErrorCount={issueFeedId ? feedById.get(issueFeedId)?.fetchErrorCount : undefined}
        fetchFailingSinceAt={issueFeedId ? feedById.get(issueFeedId)?.fetchFailingSinceAt : undefined}
        feedTitle={issueFeedId ? feedById.get(issueFeedId)?.shortTitle || feedById.get(issueFeedId)?.title : undefined}
        closeFocus={issueFeedId ? `mfIssueBadge-${issueFeedId}` : undefined}
        onClose={() => setIssueFeedId(null)}
      />

      <FeedInfoPopup
        feed={feedDetailsId ? feedById.get(feedDetailsId) : null}
        onClose={() => setFeedDetailsId(null)}
      />

      {limitUnreadPopup}

      {confirmAll && (
        <ConfirmListModal
          show
          title="Mark all feeds"
          confirmLabel="Mark as read"
          message={confirmAll.message}
          items={[]}
          onClose={() => setConfirmAll(null)}
          onConfirm={() => { confirmAll.onConfirm(); setConfirmAll(null); }}
        />
      )}
    </div>
  );

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
      <MainLayout selectedFeed="myfeeds" right={right} startWithLeftCollapsed={typeof window !== 'undefined' && window.innerWidth <= 1240} />
    </FlowFormLeaveContext.Provider>
  );
};

// --- Modal subcomponents ---

const CreateFolderModal: React.FC<{
  show: boolean; existing: string[]; autoAddCount: number;
  onClose: () => void; onCreate: (name: string) => void;
}> = ({ show, existing, autoAddCount, onClose, onCreate }) => {
  const [value, setValue] = useState('');
  useEffect(() => { if (show) setValue(''); }, [show]);
  const trimmed = value.trim();
  const canSave = !!trimmed && !existing.includes(trimmed);
  return (
    <ModalDialog show={show} title="Create folder" closeButton="close" onClose={onClose} className="mfModal">
      {autoAddCount > 0 && <p>The {autoAddCount} selected feed{autoAddCount == 1 ? '' : 's'} will be added to this folder.</p>}
      <FlowForm>
        <Input value={value} maxLength={32} placeholder="Folder name" onInput={v => setValue(v)} onEnter={() => canSave && onCreate(trimmed)} layout="singleCol" />
        <ButtonBar className="full-margin-top" align="right">
          <TextButton type="minor" onClick={onClose} label="Cancel" />
          <TextButton type="major" focussable="sideway" onClick={() => canSave && onCreate(trimmed)} label="Create" enabled={canSave ? 'enabled' : 'disabled'} />
        </ButtonBar>
      </FlowForm>
    </ModalDialog>
  );
};

const MoveToFolderModal: React.FC<{
  show: boolean; folders: string[]; currentFolder: string | null; canCreate: boolean;
  onClose: () => void; onPick: (folder: string | null) => void; onCreateAndMove: (name: string) => void;
}> = ({ show, folders, currentFolder, canCreate, onClose, onPick, onCreateAndMove }) => {
  const [newName, setNewName] = useState('');
  useEffect(() => { if (show) setNewName(''); }, [show]);
  const trimmed = newName.trim();
  const canCreateNew = canCreate && !!trimmed && !folders.includes(trimmed);
  return (
    <ModalDialog show={show} title="Move to folder" closeButton="close" onClose={onClose} className="mfModal">
      <FlowForm>
        <div className="mfFolderChoice">
          {currentFolder != null && <button className="mfFolderChoiceItem flowFocussable" tabIndex={0} onClick={() => onPick(null)} onKeyDown={getKeyHandler(() => onPick(null))}>(No folder)</button>}
          {folders.filter(f => f != currentFolder).map(f => (
            <button key={f} className="mfFolderChoiceItem flowFocussable" tabIndex={0} onClick={() => onPick(f)} onKeyDown={getKeyHandler(() => onPick(f))}>{f}</button>
          ))}
        </div>
        <Input value={newName} maxLength={32} placeholder="New folder name" onInput={v => setNewName(v)} onEnter={() => canCreateNew && onCreateAndMove(trimmed)} layout="singleCol" />
        <ButtonBar className="full-margin-top" align="right">
          <TextButton type="minor" onClick={onClose} label="Cancel" />
          <TextButton type="major" focussable="sideway" onClick={() => canCreateNew && onCreateAndMove(trimmed)} label="Create & move" enabled={canCreateNew ? 'enabled' : 'disabled'} />
        </ButtonBar>
      </FlowForm>
    </ModalDialog>
  );
};

const MergeFolderModal: React.FC<{
  show: boolean; source: string; targets: string[];
  onClose: () => void; onMerge: (target: string) => void;
}> = ({ show, source, targets, onClose, onMerge }) => (
  <ModalDialog show={show} title="Merge folder" closeButton="close" onClose={onClose} className="mfModal">
    <FlowForm>
      <p>Move all feeds from "<strong>{source}</strong>" into:</p>
      <div className="mfFolderChoice">
        {targets.map(t => <button key={t} className="mfFolderChoiceItem flowFocussable" tabIndex={0} onClick={() => onMerge(t)} onKeyDown={getKeyHandler(() => onMerge(t))}>{t}</button>)}
      </div>
      <ButtonBar className="full-margin-top" align="right">
        <TextButton type="minor" onClick={onClose} label="Cancel" />
      </ButtonBar>
    </FlowForm>
  </ModalDialog>
);

const OlderKeepModal: React.FC<{
  show: boolean; defaultDays: number; defaultKeep: number;
  onClose: () => void; onSubmit: (days: number, keep: number) => void;
}> = ({ show, defaultDays, defaultKeep, onClose, onSubmit }) => {
  const [days, setDays] = useState('');
  const [keep, setKeep] = useState('');
  useEffect(() => { if (show) { setDays(String(defaultDays)); setKeep(String(defaultKeep)); } }, [show, defaultDays, defaultKeep]);
  const d = parseInt(days, 10);
  const k = parseInt(keep, 10);
  const valid = Number.isInteger(d) && d >= 0 && d <= 36500 && Number.isInteger(k) && k >= 0 && k <= 100000;
  return (
    <ModalDialog show={show} title="Mark old posts as read" closeButton="close" onClose={onClose} className="mfModal">
      <p>Mark posts as read once they are older than the given number of days - but always keep at least the newest few unread.</p>
      <FlowForm>
        <div className="mfOkField">
          <label>Older than (days)</label>
          <div className="mfOkInput"><Input type="number" value={days} min={0} onInput={setDays} layout="singleCol" /></div>
        </div>
        <div className="mfOkField">
          <label>But keep at least (posts)</label>
          <div className="mfOkInput"><Input type="number" value={keep} min={0} onInput={setKeep} onEnter={() => valid && onSubmit(d, k)} layout="singleCol" /></div>
        </div>
        <ButtonBar className="full-margin-top" align="right">
          <TextButton type="minor" onClick={onClose} label="Cancel" />
          <TextButton type="major" focussable="sideway" onClick={() => valid && onSubmit(d, k)} label="Mark as read" enabled={valid ? 'enabled' : 'disabled'} />
        </ButtonBar>
      </FlowForm>
    </ModalDialog>
  );
};

const ConfirmListModal: React.FC<{
  show: boolean; title: string; confirmLabel: string; message: string; items: string[];
  onClose: () => void; onConfirm: () => void;
}> = ({ show, title, confirmLabel, message, items, onClose, onConfirm }) => (
  <ModalDialog show={show} title={title} closeButton="close" onClose={onClose} className="mfModal">
    <p>{message}</p>
    {items.length > 0 && <ul className="mfModalList">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>}
    <ButtonBar className="full-margin-top" align="right">
      <TextButton type="minor" onClick={onClose} label="Cancel" />
      <TextButton type="major" focussable="sideway" onClick={onConfirm} label={confirmLabel} />
    </ButtonBar>
  </ModalDialog>
);

export default MyFeedsPage;
